import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Globe } from "lucide-react";
import { useLevelBrowserPolicies } from "@/hooks/useLevelBrowserPolicies";

interface LevelBrowserWhitelistContentProps {
  levelId: string;
}

export const LevelBrowserWhitelistContent = ({ levelId }: LevelBrowserWhitelistContentProps) => {
  const [newDomain, setNewDomain] = useState("");
  const { browserPolicy, updateBrowserPolicy, isLoading } = useLevelBrowserPolicies(levelId);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    
    try {
      const currentAllowlist = browserPolicy?.allowlist || [];
      
      // Check if domain already exists
      if (currentAllowlist.includes(newDomain)) {
        return; // Domain already exists
      }

      // Check if domain is in blacklist
      if (browserPolicy?.blocklist?.includes(newDomain)) {
        // Could show a warning here
        return;
      }

      await updateBrowserPolicy({
        allowlist: [...currentAllowlist, newDomain]
      });
      
      setNewDomain("");
    } catch (error) {
      console.error('Error adding domain to allowlist:', error);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      const currentAllowlist = browserPolicy?.allowlist || [];
      await updateBrowserPolicy({
        allowlist: currentAllowlist.filter(d => d !== domain)
      });
    } catch (error) {
      console.error('Error removing domain from allowlist:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const allowedDomains = browserPolicy?.allowlist || [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Whitelisted Domains</h3>
        <p className="text-sm text-gray-600">Manage allowed domains for this level</p>
      </div>

      {/* Add Domain Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add Domain</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter domain (e.g., example.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              className="flex-1"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddDomain();
                }
              }}
            />
            <Button onClick={handleAddDomain} disabled={!newDomain.trim()}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Whitelisted Domains Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Whitelisted Domains</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {allowedDomains.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Globe className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">No whitelisted domains</p>
              <p className="text-xs text-gray-400">Add domains above to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allowedDomains.map((domain, index) => (
                  <TableRow key={index} className="bg-green-50 hover:bg-green-100">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{domain}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="success">Allowed</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveDomain(domain)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-900 mb-2">About Whitelisting</h4>
          <p className="text-sm text-blue-800">
            Domains in the whitelist are explicitly allowed for users in this level. 
            These domains will be accessible regardless of category restrictions.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};